/**
 * @fileoverview Public sub-owner invite acceptance — the page reached from the
 * invite email (`/sub-owner-invite/<token>`). Peeks the invite, lets the
 * invitee set a password, then sends them to sign in.
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import Button from '../../src/components/Button';
import Input from '../../src/components/Input';
import ContentContainer from '../../src/components/ContentContainer';
import { api, ApiError } from '../../src/api/client';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, type ColorTheme } from '../../src/utils/theme';

interface InvitePeek { email: string; name: string; businessName: string }

export default function SubOwnerInviteScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const router = useRouter();
  const { token } = useLocalSearchParams<{ token?: string }>();

  const [peek, setPeek] = useState<InvitePeek | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing invite token'); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const p = await api<InvitePeek>(`/api/public/sub-owner-invites/${encodeURIComponent(token)}/peek`, { skipAuth: true });
        if (!cancelled) setPeek(p);
      } catch (e: any) {
        if (!cancelled) setError(e instanceof ApiError ? (e.message || 'Invite not found') : 'Invite not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleAccept = async () => {
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await api(`/api/public/sub-owner-invites/${encodeURIComponent(token!)}`, {
        method: 'POST', body: { password }, skipAuth: true,
      });
      setDone(true);
    } catch (e: any) {
      setError(e instanceof ApiError ? (e.message || 'Could not accept invite') : 'Could not accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>;
  }

  return (
    <>
      <Stack.Screen options={{ title: 'Accept Invite' }} />
      <View style={styles.center}>
        <ContentContainer maxWidth={440}>
          <View style={styles.card}>
            {done ? (
              <>
                <Ionicons name="checkmark-circle" size={64} color={colors.success} style={{ alignSelf: 'center' }} />
                <Text style={styles.title}>You're all set</Text>
                <Text style={styles.subtitle}>
                  Sign in with {peek?.email} and your new password to manage {peek?.businessName}.
                </Text>
                <Button title="Sign In" onPress={() => router.replace('/login')} style={styles.btn} />
              </>
            ) : !peek ? (
              <>
                <Ionicons name="alert-circle-outline" size={64} color={colors.danger} style={{ alignSelf: 'center' }} />
                <Text style={styles.title}>Invite not available</Text>
                <Text style={styles.subtitle}>{error ?? 'This invite link is invalid or has expired.'}</Text>
                <Button title="Go to RinsePay" onPress={() => router.replace('/')} style={styles.btn} />
              </>
            ) : (
              <>
                <Ionicons name="people" size={56} color={colors.primary} style={{ alignSelf: 'center' }} />
                <Text style={styles.title}>Join {peek.businessName}</Text>
                <Text style={styles.subtitle}>
                  Hi {peek.name} — set a password for <Text style={{ fontWeight: '700' as any }}>{peek.email}</Text> to activate your login.
                </Text>
                <Input label="Password" value={password} onChangeText={setPassword} secureTextEntry autoCapitalize="none" />
                <Input label="Confirm password" value={confirm} onChangeText={setConfirm} secureTextEntry autoCapitalize="none" />
                <Text style={styles.hint}>At least 8 characters, with upper/lowercase, a number, and a symbol.</Text>
                {error && <Text style={styles.error}>{error}</Text>}
                <Button title="Activate Login" onPress={handleAccept} loading={submitting} style={styles.btn} />
              </>
            )}
          </View>
        </ContentContainer>
      </View>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  center: { flex: 1, backgroundColor: c.background, justifyContent: 'center', padding: spacing.lg },
  card: {
    backgroundColor: c.surface, borderRadius: 16, padding: spacing.xl, width: '100%',
    shadowColor: '#000', shadowOpacity: 0.05, shadowRadius: 8, shadowOffset: { width: 0, height: 2 }, elevation: 2,
  },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold as any, color: c.text, textAlign: 'center', marginTop: spacing.md },
  subtitle: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', marginTop: spacing.sm, marginBottom: spacing.lg, lineHeight: 20 },
  hint: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs },
  error: { fontSize: fontSize.sm, color: c.danger, marginTop: spacing.sm, textAlign: 'center' },
  btn: { marginTop: spacing.md, width: '100%' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
